import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getNextStep, getPrevStep } from '../../../utils/steps';
import { ELandingType } from '../../../types/landingType';
import { ILogger, TError } from '@cian/logger/shared';
import { TMeta } from '@cian/logger/shared/types';
import { acceptPublicOffer } from '../../../thunks/joinOtaIndividual/acceptPublicOffer';
import { saveFormAndAcceptOffer } from '../../../thunks/joinOtaIndividual/saveFormAndAcceptOffer';
import { EBusinessEntityStep } from '../../../types/businessEntityFlow/step';

type TPayload = ELandingType;

interface IErrorPayload {
  logger: ILogger;
  error: TError;
  extra?: TMeta;
}

export interface IStepsSlice {
  activeStep: EBusinessEntityStep;
  steps: EBusinessEntityStep[];
}

const initialState: IStepsSlice = {
  activeStep: EBusinessEntityStep.Unexpected,
  steps: [EBusinessEntityStep.Unexpected],
};

const stepsConfigurationSlice = createSlice({
  name: 'entrepreneurFlow/steps',
  initialState,
  reducers: {
    setStepsConfiguration(state, action: PayloadAction<IStepsSlice>) {
      state.steps = action.payload.steps;
      state.activeStep = action.payload.activeStep;
    },
    goToNextStep(state, action: PayloadAction<TPayload>) {
      state.activeStep = getNextStep(
        state.steps,
        EBusinessEntityStep.Unexpected,
        action.payload,
        state.activeStep,
      ) as EBusinessEntityStep;
    },
    goToPrevStep(state, action: PayloadAction<TPayload>) {
      state.activeStep = getPrevStep(
        state.steps,
        EBusinessEntityStep.Unexpected,
        action.payload,
        state.activeStep,
      ) as EBusinessEntityStep;
    },
    goToErrorStep: (state, action: PayloadAction<IErrorPayload>) => {
      const { logger, error, extra } = action.payload;
      logger.error(error, extra);

      state.activeStep = EBusinessEntityStep.Error;
    },
  },
  extraReducers(builder) {
    builder.addCase(acceptPublicOffer.rejected, state => {
      state.activeStep = EBusinessEntityStep.Error;
    });
    builder.addCase(saveFormAndAcceptOffer.rejected, (state, action) => {
      if (action.payload?.type === 'unknownError') {
        state.activeStep = EBusinessEntityStep.Error;
      }
    });
  },
});

export const {
  reducer: stepsConfigurationReducer,
  actions: { goToNextStep, goToPrevStep, goToErrorStep, setStepsConfiguration },
} = stepsConfigurationSlice;
